import { useMemo, useState, useCallback } from "react";

import { Button } from "@/components/Button";
import { useAppProperties, useOffers } from "@/hooks";
import { CreditCard } from "@/icons";
import { useAuthToken, useTranslate } from "@/providers/context";
import { getDateFromTimestamp } from "@/utils";
import { useCancelExternalSubscriptions } from "../hooks/useCancelExternalSubscriptions";
import { ColumnModal } from "@/components";
import { Offer } from "@/api";

export const ExternalPlan = ({
  oldPlatform,
  products,
}: {
  oldPlatform: string;
  products: { oldProductId: string; endTs?: number; sourceProductId: string }[];
}) => {
  const { translate, formatDate } = useTranslate();
  const { oldProductNames, externalCancelURL, externalManageURL } = useAppProperties();
  const { data: offers } = useOffers();
  const cancelExternalSubscription = useCancelExternalSubscriptions();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { refresh, isRefreshing } = useAuthToken();

  const oldProductMap = useMemo(() => {
    try {
      return oldProductNames ? JSON.parse(oldProductNames) : {};
    } catch {
      return {};
    }
  }, [oldProductNames]);
  const sourceProductMap = useMemo(() => {
    return (
      offers?.reduce(
        (acc, offer) => {
          acc[offer.SourceProductId] = offer;
          return acc;
        },
        {} as Record<string, Offer>,
      ) ?? {}
    );
  }, [offers]);

  const getProductName = (product: {
    oldProductId: string;
    sourceProductId: string;
  }) => {
    const oldId = product.oldProductId || "";
    const sourceId = product.sourceProductId || "";

    if (oldProductMap && oldProductMap[oldId]) {
      return oldProductMap[oldId];
    }

    if (
      sourceProductMap &&
      sourceId &&
      sourceProductMap[sourceId]?.FinalDisplayName
    ) {
      return sourceProductMap[sourceId].FinalDisplayName;
    }

    return `${oldPlatform || "External"} Subscription`;
  };

  // Check if all products don't have an endTs (are active and can be cancelled)
  const hasActiveCancellableProducts = useMemo(() => {
    return products.length > 0 && products.every((product) => !product.endTs);
  }, [products]);

  const handleConfirm = useCallback(async () => {
    if (externalCancelURL) {
      // Redirect to external cancellation URL
      window.open(externalCancelURL, '_blank');
      setShowCancelModal(false);
    } else {
      // Use existing API cancellation flow
      await cancelExternalSubscription.mutateAsync({
        old_platform: oldPlatform,
      });
      refresh({
        onSettled: () => {
          setShowCancelModal(false);
        },
      });
    }
  }, [externalCancelURL, cancelExternalSubscription, oldPlatform, refresh]);

  const handleManage = useCallback(async () => {
    if (externalManageURL) {
      window.open(externalManageURL, '_blank');
    }
  }, [externalManageURL]);

  return (
    <>
      <div className="mb-4 flex w-full flex-col rounded-lg border border-gray-200 px-4 py-6">
        <div className="flex flex-col gap-4">
          {products.map((product) => (
            <div key={product.oldProductId} className="flex flex-col gap-2">
              <div className="h4-bold">{getProductName(product)}</div>
              <div className="p2-regular">
                {product.endTs
                  ? `${translate("Active until")}: ${formatDate(
                      getDateFromTimestamp(product.endTs),
                      true,
                    )}`
                  : `${translate("Active")}`}
              </div>
            </div>
          ))}
        </div>
        {externalManageURL ? (
          <div className="p2-regular border-borderColor mt-8 border-t pt-8">
            <Button
              size="s"
              variant="tertiaryHighlight"
              className="text-highlightPrimary"
              onClick={handleManage}
              icon={<CreditCard />}
              iconPosition="left"
            >
              {translate("Manage My Subscription")}
            </Button>
          </div>
        ) : (
          ["Kajabi", "Shopify", "PayPal"].includes(oldPlatform) &&
          hasActiveCancellableProducts && (
            <div className="p2-regular border-borderColor mt-8 border-t pt-8">
              <Button
                size="s"
                variant="tertiaryHighlight"
                className="text-highlightPrimary"
                onClick={() => setShowCancelModal(true)}
                icon={<CreditCard />}
                iconPosition="left"
              >
                {translate("Cancel My Subscription")}
              </Button>
            </div>
          )
        )}
      </div>

      <ColumnModal
        open={showCancelModal}
        onOpenChange={setShowCancelModal}
        title={translate(
          "Are you sure you want to cancel the following subscriptions?",
        )}
        primaryText={translate("Confirm")}
        secondaryText={translate("Cancel")}
        onConfirm={handleConfirm}
        onCancel={() => setShowCancelModal(false)}
        confirmIsLoading={cancelExternalSubscription.isLoading || isRefreshing}
        cols={4}
        hideTrigger
      >
        <div className="p2-regular px-6 py-4">
          <div className="mb-6">
            <ul className="list-none pl-2">
              {products.map((product) => (
                <li
                  key={product.oldProductId}
                  className="mb-2 flex items-center"
                >
                  <span className="mr-2">-</span>
                  <span className="p2-medium">{getProductName(product)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            {translate(
              "Your content access will be revoked at the end of the current billing period.",
            )}
          </div>
        </div>
      </ColumnModal>
    </>
  );
};
